/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import React from 'react';
import dayjs from 'dayjs';

import { DateInput, Form, Grid } from '@mairistem/composants';
import { AdresseForm } from '@mairistem/adresses';

import { SectionHeader } from '@/components/Common';
import { useAdresseFormFiche } from '@/hooks';

import PanelModalEdition from '../../PanelModalEdition';
import CommuneDelegueField from '../CommuneDelegueField';

interface InformationsDeclarationSignatureModalProps {
  data: any;
  onSave: () => Promise<void>;
  onChange: (data: Record<string, unknown>) => void;
  onClose: () => void;
  onCancel: () => void;
}

const InformationsDeclarationSignatureModal = (
  props: InformationsDeclarationSignatureModalProps,
) => {
  const { data, onSave, onChange, onClose, onCancel } = props;

  const [loading, setLoading] = React.useState<boolean>(false);
  const [errors, setErrors] = React.useState<
    Record<string, string | boolean> & { api?: boolean }
  >({});

  const handleChangeDate = React.useCallback(
    (date: string | Date | undefined) => {
      onChange({
        dateDeclaration: date ? dayjs(date).format('YYYY-MM-DD') : null,
      });
    },
    [onChange],
  );

  const handleAdresseChange = React.useCallback(
    (adresse: Record<string, unknown>) =>
      onChange({
        adresseDeclaration: { ...data?.adresseDeclaration, ...adresse },
      }),
    [onChange, data?.adresseDeclaration],
  );
  const adresseForm = useAdresseFormFiche(
    data?.adresseDeclaration,
    handleAdresseChange,
  );

  const handleChangeCommuneDelegue = React.useCallback(
    (libelle: string, drapeau: boolean) => {
      onChange({
        adresseDeclaration: {
          ...data?.adresseDeclaration,
          communeDelegue: libelle,
          estCommuneDelegue: drapeau,
        },
      });
    },
    [onChange, data?.adresseDeclaration],
  );

  const handleSave = React.useCallback(() => {
    setLoading(true);

    onSave()
      .then(() => {
        onClose();
      })
      .catch(() => {
        setErrors({ api: true });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [onClose, onSave]);

  return (
    <PanelModalEdition
      data-testid="declaration-signature-modal"
      errors={errors}
      loading={loading}
      header="SIGNATURE DE LA DÉCLARATION"
      onSave={handleSave}
      onClose={onCancel}
    >
      <Form>
        <Grid>
          <Grid.Row>
            <Grid.Column width={16}>
              <SectionHeader title="SIGNATURE" style={{ marginTop: 0 }} />
              <Form.Field
                style={{
                  margin: '1em',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                }}
              >
                <label>Déclaration signée le</label>
                <DateInput
                  data-testid="declaration-signature-date"
                  value={
                    data?.dateDeclaration
                      ? new Date(data.dateDeclaration)
                      : undefined
                  }
                  onChange={handleChangeDate}
                />
              </Form.Field>
              <SectionHeader
                title="LIEU DE SIGNATURE"
                style={{ marginTop: 0 }}
              />
              <div style={{ margin: '1em' }}>
                <AdresseForm
                  saisie="libre"
                  handleTypes
                  displayDepartement={false}
                  displayArticle
                  adresse={adresseForm.adresseFormValue}
                  onItemChange={adresseForm.onItemChange}
                  onChange={adresseForm.onChange}
                />
                {/* Même espacement (2em) qu'entre les lignes d'AdresseForm */}
                <div style={{ marginTop: '2em' }}>
                  <CommuneDelegueField
                    data-testid="declaration-signature-commune-delegue"
                    libelle={data?.adresseDeclaration?.communeDelegue}
                    drapeau={data?.adresseDeclaration?.estCommuneDelegue}
                    onChange={handleChangeCommuneDelegue}
                  />
                </div>
              </div>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Form>
    </PanelModalEdition>
  );
};

export default InformationsDeclarationSignatureModal;
